import { Component, input, output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PopoverModule } from 'primeng/popover';
import { AvatarModule } from 'primeng/avatar';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { User } from '../../../models/user';
import { Language } from '../../../core/constants';
import { AuthService } from '../../../core/services';
import {
  HelpCenterComponent,
  BalanceHistoryComponent,
} from '../../dialogs';

@Component({
  selector: 'app-user-popover',
  standalone: true,
  imports: [
    CommonModule,
    PopoverModule,
    AvatarModule,
    TranslocoPipe,
  ],
  templateUrl: './user-popover.component.html',
  styleUrls: ['./user-popover.component.scss'],
  providers: [DialogService],
})
export class UserPopoverComponent {
  // Inputs
  currentUser = input<User | null>(null);
  currentUserBalance = input<string>('0');
  balanceLoading = input<boolean>(false);
  isDark = input<boolean>(false);
  currentLanguage = input<Language | null>(null);
  availableLanguages = input<Language[]>([]);
  appVersion = input<string>('1.0.0');
  userPopoverRef = input<any>(null); // Reference to the popover instance

  // Outputs
  profileClick = output<void>();
  balanceHistoryClick = output<void>();
  helpCenterClick = output<void>();
  settingsClick = output<void>();
  logoutClick = output<void>();
  languageSelect = output<Language>();

  private router = inject(Router);
  private dialogService = inject(DialogService);
  private translocoService = inject(TranslocoService);
  private authService = inject(AuthService);

  goToProfile() {
    this.hidePopover();
    this.profileClick.emit();
    this.router.navigate(['/profile']);
  }

  goToBalanceHistory() {
    this.dialogService.open(BalanceHistoryComponent, {
      header: this.translocoService.translate('header.user.balanceHistory'),
      width: '100vw',
      styleClass: 'balance-history-dialog',
      modal: true,
      maximizable: true,
      dismissableMask: true,
      closable: true,
    });
    this.hidePopover();
    this.balanceHistoryClick.emit();
  }

  goToHelpCenter() {
    this.dialogService.open(HelpCenterComponent, {
      header: this.translocoService.translate('header.user.helpCenter'),
      width: '60vw',
      styleClass: 'help-center-dialog',
      modal: true,
      dismissableMask: true,
      closable: true,
    });
    this.hidePopover();
    this.helpCenterClick.emit();
  }

  goToSettings() {
    this.hidePopover();
    this.settingsClick.emit();
    this.router.navigate(['/settings']);
  }

  logout() {
    this.hidePopover();
    this.logoutClick.emit();
  }

  selectLanguage(language: Language) {
    this.hidePopover();
    this.languageSelect.emit(language);
  }

  goToChangePassword() {
    this.hidePopover();
    this.router.navigate(['/change-password']);
  }

  get ldapEnabled(): boolean {
    return this.authService.currentUser?.ldap_enabled ?? false;
  }

  private hidePopover() {
    if (this.userPopoverRef()) {
      this.userPopoverRef().hide();
    }
  }

  getCurrentUserBalance(): string {
    return (Number(this.currentUserBalance()) > 0) ? this.currentUserBalance() : '0';
  }
}

